import { ErrorResponseDto } from "api/config/ErrorResponseDto";
import LoadingScreen from "components/Loading/LoadingScreen";
import { useLayoutStore, useRoleStore, useUserRolePermissionStore } from "contexts/RootStore.context";
import useDevice from "hooks/useDevice";
import { isString } from "lodash";
import { observer } from "mobx-react";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { configRoutePath } from "router/routePaths";
import { alertErrorMessage, alertSuccessMessage } from "utils/alerts";
import WidthNotSupportedPage from "views/WidthNotSupportedPage";
import { RoleForm } from "./RoleForm";
import NoPermissionsPage from "views/NoPermissionsPage";

const Role = observer(() => {

  const params = useParams<{ roleId: string | undefined }>();
  const editMode = isString(params.roleId) ? false : true;

  const {
    dataLoading,
    dataLoadingError,
    LoadData,
    roleForm,
    setCurrentRoleId,
    reset,
    SaveRole,
  } = useRoleStore();

  const navigate = useNavigate();
  const { isMobileDevice } = useDevice();
  const { setPageError } = useLayoutStore()
  const { canManageWorkgroupRoles, canViewWorkgroupRoles, canManageGroupsAndRoles, canViewConfig } = useUserRolePermissionStore()

  const manageAccess = canManageGroupsAndRoles || canManageWorkgroupRoles

  useEffect(() => {
    const abortController = new AbortController();
    setCurrentRoleId(params.roleId);
    LoadData(params.roleId)
      .finally(() => {
        if (abortController.signal.aborted) {
          reset();
        }
      });

    return () => {
      abortController.abort();
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, params.roleId]);

  useEffect(() => {
    setPageError(dataLoadingError)
  }, [dataLoadingError, setPageError])

  const saveHandler = async () => {
    SaveRole(params.roleId)
      .then(() => {
        navigate(configRoutePath.listRoles);
        alertSuccessMessage(!params.roleId
          ? `${roleForm.$.name.value} Role has been created`
          : `Changes saved for the ${roleForm.$.name.value} role`)
      })
      .catch((error: ErrorResponseDto) => {
        alertErrorMessage(error.errorMsg)
      })
  }

  if (!canViewConfig && !canViewWorkgroupRoles) {
    return <NoPermissionsPage />
  }

  if (isMobileDevice) {
    return <WidthNotSupportedPage />;
  }

  if (dataLoading) {
    return <LoadingScreen />;
  }

  return (
    <RoleForm
      saveHandler={saveHandler}
      canManageRoles={manageAccess}
      roleId={params.roleId}
    />
  );
});

export default Role;